<template>
  <form @submit.prevent="confirmUpdate()" action="">
    <ion-row>
      <ion-col size="12">
        <ion-list>
          <ion-item v-if="validationError" lines="none">
            <ion-label className="ion-text-wrap">
              <ion-text color="danger">
                <p v-for="error of v$.$errors" :key="error.$uid">
                  <ion-icon
                    :ios="alertCircleOutline"
                    :md="alertCircleSharp"
                    size="small"
                    style="vertical-align: middle"
                  ></ion-icon
                  >{{ error.$property }}: {{ error.$message }}
                </p></ion-text
              >
            </ion-label>
          </ion-item>

          <ion-item :class="{ 'color-danger': v$.name.$error }">
            <ion-label position="floating">Name</ion-label>
            <ion-input
              type="text"
              v-model="name"
              placeholder="Admission fee for 20XX.."
            ></ion-input>
          </ion-item>

          <ion-item :class="{ 'color-danger': v$.chargeable_ids.$error }">
            <ion-label position="floating">Chargeables</ion-label>
            <ion-select
              v-model="chargeable_ids"
              :value="chargeable_ids"
              multiple
            >
              <ion-select-option
                v-for="chargeable of chargeables"
                :key="chargeable.id"
                :value="chargeable.id"
              >
                {{ chargeable.name }}
              </ion-select-option>
            </ion-select>
          </ion-item>

          <ion-item :class="{ 'color-danger': v$.standard_ids.$error }">
            <ion-label position="floating">Standards</ion-label>
            <ion-select v-model="standard_ids" :value="standard_ids" multiple>
              <ion-select-option
                v-for="standard of standards"
                :key="standard.id"
                :value="standard.id"
              >
                Standard {{ standard.name }}
              </ion-select-option>
            </ion-select>
          </ion-item>
        </ion-list>
      </ion-col>
    </ion-row>
    <ion-row>
      <ion-col>
        <ion-button type="submit" expand="full" class="ion-margin-vertical">
          Save
        </ion-button>
      </ion-col>
    </ion-row>
  </form>
</template>

<script>
import {
  IonRow,
  IonCol,
  IonList,
  IonLabel,
  IonItem,
  IonButton,
  IonSelect,
  IonSelectOption,
  IonInput,
  IonText,
  IonIcon,
  loadingController,
  alertController,
} from "@ionic/vue";

import { alertCircleOutline, alertCircleSharp } from "ionicons/icons";

import { required, maxLength, helpers } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";

export default {
  props: {
    resource: {
      type: Object,
      required: true,
    },
  },
  emits: ["closeModal"],
  components: {
    IonRow,
    IonCol,
    IonList,
    IonLabel,
    IonItem,
    IonButton,
    IonSelect,
    IonSelectOption,
    IonInput,
    IonText,
    IonIcon,
  },
  data() {
    return {
      id: this.resource.id,
      name: this.resource.name,
      chargeable_ids: null,
      standard_ids: null,

      alertCircleOutline,
      alertCircleSharp,

      validationError: null,
      xhrError: {
        header: null,
        description: null,
      },
    };
  },

  computed: {
    chargeables() {
      return this.$store.getters["chargeable/allResources"];
    },
    standards() {
      return this.$store.getters["standard/allResources"];
    },
  },

  setup() {
    return { v$: useVuelidate() };
  },

  validations() {
    return {
      name: {
        required: helpers.withMessage("cannot be empty", required),
        maxLength: helpers.withMessage(
          "maximum 255 characters",
          maxLength(255)
        ),
      },
      chargeable_ids: {
        required: helpers.withMessage("is required", required),
      },

      standard_ids: {
        required: helpers.withMessage("is required", required),
      },
    };
  },

  methods: {
    setModalState(state) {
      this.modalState = state;
    },

    loader(message) {
      const loading = loadingController.create({
        message: message,
        backdropDismiss: false,
      });

      return loading;
    },

    alerter(header, message, buttons = ["close"]) {
      const alert = alertController.create({
        header: header,
        message: message,
        buttons: buttons,
      });

      return alert;
    },

    setErrorResponse(error) {
      if (error.response) {
        this.xhrError.header = error.response.data.header;
        this.xhrError.message = error.response.data.message;
      } else if (error.request) {
        this.xhrError.header = error.message || error.request;
        this.xhrError.message = "Please check your connection and try again";
      } else {
        this.xhrError.header = error.message;
        this.xhrError.message = "Something went wrong. Try again later";
      }
    },

    async confirmUpdate() {
      const header = `Confirm`;
      const message = "Are you sure you want to save changes?";
      const buttons = [
        {
          text: "Cancel",
          role: "cancel",
          cssClass: "secondary",
        },
        {
          text: "Okay",
          handler: () => {
            this.updateFee();
          },
        },
      ];
      const alertDialog = await this.alerter(header, message, buttons);

      alertDialog.present();
    },

    async updateFee() {
      this.v$.$touch();
      if (this.v$.$error) {
        this.validationError = true;
        return;
      }

      let data = {
        id: this.id,
        name: this.name,
        chargeable_ids: this.chargeable_ids,
        standard_ids: this.standard_ids,
      };

      const spinner = await this.loader("Saving...");
      spinner.present();

      try {
        await this.$store.dispatch("fee/updateResource", data);
        spinner.dismiss();

        this.name = null;
        this.$emit("closeModal");
      } catch (error) {
        spinner.dismiss();

        this.setErrorResponse(error);

        const alertDialog = await this.alerter(
          this.xhrError.header,
          this.xhrError.message
        );

        alertDialog.present();

        this.password = null;
        this.validationError = null;
      }
    },

    async fetchChargeables() {
      await this.$store.dispatch("chargeable/allResources");

      let id_arr = [];

      this.resource.chargeables.forEach((item) => {
        id_arr.push(item.id);
      });

      this.chargeable_ids = id_arr;
    },

    async fetchStandards() {
      await this.$store.dispatch("standard/allResources");

      let id_arr = [];

      this.resource.standards.forEach((item) => {
        id_arr.push(item.id);
      });

      this.standard_ids = id_arr;
    },
  },

  created() {
    this.fetchChargeables();
    this.fetchStandards();
  },
};
</script>
